import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../../config.js";
import DownloadButtons from "../DownloadButtons/DownloadButtons.js";
import { downloadXlsx, STYLE } from "../../common/xlsxExport.js";
import "../preloading.css";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

/**
 * Department wise overtime payout sheet — one row per employee for the whole
 * date range, grouped under its department with a per department subtotal and
 * a final OT Total Amount, matching the sheet accounts prepares by hand.
 */
const DepartmentWiseOvertimeReport = () => {
  const today = new Date().toISOString().split("T")[0];

  const [filters, setFilters] = useState({
    start_date: today,
    end_date: today,
    dept_id: "0",
    overtime_status: "2",
  });

  const [departments, setDepartments] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState("");

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  /* ================= DEPARTMENTS ================= */
  const fetchDepartments = async () => {
    try {
      const res = await axios.get(API_BASE_URL + "/showdepartment", {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });

      setDepartments(res.data?.departments || res.data?.data || res.data || []);
    } catch {
      toast.error("Failed to load departments");
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  /* ================= FETCH REPORT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const res = await axios.post(
        API_BASE_URL + "/department_wise_overtime_report",
        filters,
        { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
      );

      setRecords(res.data.records || []);
      toast.success("Department wise OT report loaded successfully!", {
        toastId: "deptwise-overtime-success",
        position: "bottom-right",
        theme: "colored",
      });
    } catch (error) {
      console.error(error);
      toast.error("Error fetching department wise overtime report", {
        toastId: "deptwise-overtime-error",
      });
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };

  /* ================= SEARCH ================= */
  const filteredRecords = records.filter((item) =>
    `${item.employee_id} ${item.employee_name} ${item.designation} ${item.department} ${item.incharge_name} ${item.account_no}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  /* ================= GROUP BY DEPARTMENT ================= */
  const groups = [];
  filteredRecords.forEach((row) => {
    let group = groups.find((g) => g.department === row.department);
    if (!group) {
      group = { department: row.department, rows: [], total_hours: 0, total_paid: 0 };
      groups.push(group);
    }
    group.rows.push(row);
    group.total_hours += Number(row.ot_hours) || 0;
    group.total_paid += Number(row.to_be_paid) || 0;
  });

  const grandTotal = groups.reduce((sum, g) => sum + g.total_paid, 0);

  const money = (v) =>
    (Number(v) || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const COL_COUNT = 10;

  /* ================= CSV (same layout as the table on screen) ================= */
  const CSV_HEADERS = [
    "Emp No",
    "Name",
    "Designation",
    "A/C No",
    "Incharge",
    "OT in Hrs",
    "Actual Hrs",
    "Actual Amount",
    "Per Hrs Salary",
    "To be Paid",
  ];

  const blanks = (n) => Array(n).fill("");

  /* account no must land in the sheet as a plain number, never as text or in
     scientific notation — keep only the digits and drop anything else */
  const accountNo = (v) => String(v ?? "").replace(/\D/g, "");

  const csvData = [CSV_HEADERS];
  groups.forEach((group) => {
    csvData.push([
      `${group.department || "-"} (${group.rows.length} employee${group.rows.length > 1 ? "s" : ""})`,
      ...blanks(9),
    ]);

    group.rows.forEach((row) => {
      csvData.push([
        row.employee_id,
        row.employee_name,
        row.designation,
        accountNo(row.account_no),
        row.incharge_name,
        row.ot_hours,
        row.actual_hour,
        row.actual_amount,
        row.per_hour,
        row.to_be_paid,
      ]);
    });

    csvData.push([
      ...blanks(4),
      `${group.department} Subtotal`,
      group.total_hours.toFixed(2),
      ...blanks(3),
      group.total_paid.toFixed(2),
    ]);
  });

  if (groups.length > 0) {
    csvData.push([...blanks(4), "OT Total Amount", ...blanks(4), grandTotal.toFixed(2)]);
  }

  /* ================= EXCEL (CSV can't carry bold, so ship a real .xlsx) ================= */
  const EXCEL_COLUMNS = [
    { width: 10 }, // Emp No
    { width: 24 }, // Name
    { width: 24 }, // Designation
    { width: 20 }, // A/C No — wide enough that 14 digits never collapse to 5.01E+13
    { width: 22 }, // Incharge
    { width: 11 }, // OT in Hrs
    { width: 11 }, // Actual Hrs
    { width: 15 }, // Actual Amount
    { width: 15 }, // Per Hrs Salary
    { width: 15 }, // To be Paid
  ];

  const num = (v) => Number(v) || 0;

  const exportExcel = async () => {
    if (groups.length === 0) {
      toast.info("No data to export", { toastId: "deptwise-overtime-noexport" });
      return;
    }

    const excelRows = [CSV_HEADERS.map((h) => ({ v: h, s: STYLE.header }))];

    groups.forEach((group) => {
      excelRows.push([
        {
          v: `${group.department || "-"} (${group.rows.length} employee${
            group.rows.length > 1 ? "s" : ""
          })`,
          s: STYLE.group,
          colSpan: COL_COUNT,
        },
      ]);

      group.rows.forEach((row) => {
        excelRows.push([
          { v: num(row.employee_id), t: "n", s: STYLE.int },
          { v: row.employee_name, s: STYLE.text },
          { v: row.designation, s: STYLE.text },
          { v: accountNo(row.account_no), t: "n", s: STYLE.int },
          { v: row.incharge_name, s: STYLE.text },
          { v: num(row.ot_hours), t: "n", s: STYLE.money },
          { v: num(row.actual_hour), t: "n", s: STYLE.int },
          { v: num(row.actual_amount), t: "n", s: STYLE.money },
          { v: num(row.per_hour), t: "n", s: STYLE.money },
          { v: num(row.to_be_paid), t: "n", s: STYLE.moneyBold },
        ]);
      });

      excelRows.push([
        {
          v: `${group.department} Subtotal`,
          s: STYLE.subLabel,
          colSpan: COL_COUNT - 5,
        },
        { v: group.total_hours, t: "n", s: STYLE.subValue },
        { v: "", s: STYLE.subBlank, colSpan: 3 },
        { v: group.total_paid, t: "n", s: STYLE.subValue },
      ]);
    });

    excelRows.push([
      { v: "OT Total Amount", s: STYLE.totalLabel, colSpan: COL_COUNT - 1 },
      { v: grandTotal, t: "n", s: STYLE.totalValue },
    ]);

    try {
      await downloadXlsx({
        filename: "Department_Wise_Overtime",
        sheetName: "Department Wise OT",
        columns: EXCEL_COLUMNS,
        rows: excelRows,
      });
    } catch (error) {
      console.error(error);
      toast.error("Error creating the Excel file", {
        toastId: "deptwise-overtime-excel-error",
      });
    }
  };

  return (
    <div className="container-fluid mt-4">
      {/* ================= FILTER ================= */}
      <div className="card shadow-sm mb-4">
        <div className="card-header with-elements">
          <h3
            className="card-title d-flex align-items-center mb-0"
            style={{ color: "#1d3a8a", fontWeight: 600 }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 16 16"
              fill="#1d3a8a"
              style={{ marginRight: "8px", verticalAlign: "-3px" }}
            >
              <path d="M14.5 3a.5.5 0 0 1 .5.5v9a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5zm-13-1A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h13a1.5 1.5 0 0 0 1.5-1.5v-9A1.5 1.5 0 0 0 14.5 2z" />
              <path d="M3 5.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5m0 2.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5m0 2.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5" />
            </svg>
            Department Wise Overtime Report
          </h3>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3 align-items-end">
              {/* From Date */}
              <div className="col-md">
                <label htmlFor="start_date">From Date</label>
                <DatePicker
                  selected={filters.start_date ? new Date(filters.start_date) : null}
                  onChange={(date) =>
                    setFilters((prev) => ({
                      ...prev,
                      start_date: date ? date.toLocaleDateString("en-CA") : "",
                    }))
                  }
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select From Date"
                  className="form-control"
                  maxDate={filters.end_date ? new Date(filters.end_date) : null}
                  required
                />
              </div>

              {/* To Date */}
              <div className="col-md">
                <label htmlFor="end_date">To Date</label>
                <DatePicker
                  selected={filters.end_date ? new Date(filters.end_date) : null}
                  onChange={(date) =>
                    setFilters((prev) => ({
                      ...prev,
                      end_date: date ? date.toLocaleDateString("en-CA") : "",
                    }))
                  }
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select To Date"
                  className="form-control"
                  minDate={filters.start_date ? new Date(filters.start_date) : null}
                  required
                />
              </div>

              {/* Department */}
              <div className="col-md">
                <label htmlFor="dept_id">Department</label>
                <Select
                  name="dept_id"
                  options={departments.map((d) => ({
                    value: d.department_id || d.id,
                    label: d.department_name,
                  }))}
                  value={
                    departments
                      .map((d) => ({
                        value: d.department_id || d.id,
                        label: d.department_name,
                      }))
                      .find((o) => o.value === filters.dept_id) || null
                  }
                  onChange={(opt) =>
                    handleInput({
                      target: { name: "dept_id", value: opt ? opt.value : "0" },
                    })
                  }
                  isClearable
                  placeholder="All Department"
                  className="basic-single"
                />
              </div>

              {/* Overtime Status */}
              <div className="col-md">
                <label htmlFor="overtime_status">Overtime Status</label>
                <select
                  className="form-control"
                  style={{ height: "38px" }}
                  name="overtime_status"
                  value={filters.overtime_status}
                  onChange={handleInput}
                >
                  <option value="2">Approved</option>
                  <option value="3">Rejected</option>
                  <option value="0">Cancelled</option>
                </select>
              </div>

              <div className="col-md-auto">
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  style={{ height: "38px" }}
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Get"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between mb-2">
            <div
              className="d-flex align-items-center gap-2"
              style={{ width: "auto", minWidth: "220px" }}
            >
              <DownloadButtons data={csvData} filename="Department_Wise_Overtime" />

              <button
                type="button"
                className="btn btn-sm"
                style={{
                  backgroundColor: "white",
                  color: "black",
                  border: "1px solid black",
                  padding: "4px 10px",
                  fontSize: "14px",
                  borderRadius: "4px",
                  marginLeft: "8px", // bootstrap gap utilities aren't reliable here
                }}
                onClick={exportExcel}
              >
                EXCEL
              </button>
            </div>

            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search..."
              style={{ width: "220px" }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="d-flex justify-content-center p-5">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="table-responsive sticky-table-wrap">
              <table className="table table-bordered table-striped sticky-table sticky-head-tint sticky-no-freeze-col">
                <thead>
                  <tr>
                    <th>Emp No</th>
                    <th>Name</th>
                    <th>Designation</th>
                    <th>A/C No</th>
                    <th>Incharge</th>
                    <th className="text-end">OT in Hrs</th>
                    <th className="text-end">Actual Hrs</th>
                    <th className="text-end">Actual Amount</th>
                    <th className="text-end">Per Hrs Salary</th>
                    <th className="text-end">To be Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.length > 0 ? (
                    <>
                      {groups.map((group) => (
                        <React.Fragment key={group.department}>
                          {/* department header */}
                          <tr>
                            <td
                              colSpan={COL_COUNT}
                              style={{
                                background: "#e8eefc",
                                color: "#1d3a8a",
                                fontWeight: 700,
                              }}
                            >
                              {group.department || "—"}{" "}
                              <span className="fw-normal">
                                ({group.rows.length} employee
                                {group.rows.length > 1 ? "s" : ""})
                              </span>
                            </td>
                          </tr>

                          {group.rows.map((row, idx) => (
                            <tr key={`${group.department}-${row.employee_id}-${idx}`}>
                              <td>{row.employee_id}</td>
                              <td>{row.employee_name}</td>
                              <td>{row.designation}</td>
                              <td>{row.account_no}</td>
                              <td>{row.incharge_name}</td>
                              <td className="text-end">{row.ot_hours}</td>
                              <td className="text-end">{row.actual_hour}</td>
                              <td className="text-end">{money(row.actual_amount)}</td>
                              <td className="text-end">{money(row.per_hour)}</td>
                              <td className="text-end fw-bold">{money(row.to_be_paid)}</td>
                            </tr>
                          ))}

                          {/* department subtotal */}
                          <tr style={{ background: "#f6f8fc" }}>
                            <td colSpan={COL_COUNT - 5} className="text-end fw-bold">
                              {group.department} Subtotal
                            </td>
                            <td className="text-end fw-bold">
                              {group.total_hours.toFixed(2)}
                            </td>
                            <td colSpan="3"></td>
                            <td className="text-end fw-bold">{money(group.total_paid)}</td>
                          </tr>
                        </React.Fragment>
                      ))}

                      {/* grand total */}
                      <tr style={{ background: "#dbe4f7" }}>
                        <td
                          colSpan={COL_COUNT - 1}
                          className="text-end fw-bold"
                          style={{ color: "#1d3a8a" }}
                        >
                          OT Total Amount
                        </td>
                        <td className="text-end fw-bold" style={{ color: "#1d3a8a" }}>
                          {money(grandTotal)}
                        </td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan={COL_COUNT} className="text-center text-danger fw-bold">
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DepartmentWiseOvertimeReport;
